import { Component, signal, computed, inject, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MemberOnboardingProgressService } from '../../../core/api-services/member-onboarding-progress.service';
import { MemberOnboardingProgress } from '../../../core/response-models/member-onboarding-progress.model';
import { OrganizationContextService } from '../../../core/services/organization-context.service';
import { OrganizationService } from '../../../core/data-services/organization.service';
import { CertificationService } from '../../../core/data-services/certification.service';
import { AuthApiService } from '../../../core/api-services/auth-api.service';
import { ApiError } from '../../../abstract/api-error';
import { ToastService } from '../../../core/services/toast.service';
import { NgxMaskDirective } from 'ngx-mask';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { UsStateSelectComponent } from '../us-state-select/us-state-select.component';

/**
 * Onboarding wizard step
 */
interface WizardStep {
  stepNumber: number;
  title: string;
  subtitle: string;
  isRequired: boolean;
  isCompleted: boolean;
}

/**
 * Onboarding Wizard Modal Component
 *
 * Multi-step wizard guiding new members through onboarding process.
 *
 * Steps:
 * 1. Complete Profile (phone, address - DOB optional)
 * 2. Upload Part 107 Certificate (REQUIRED)
 * 3. Upload FAA Medical Certificate (optional)
 * 4. Upload Insurance Certificate (optional)
 * 5. Upload State Applicator License (REQUIRED)
 * 6. Review Safety Guidelines (REQUIRED)
 * 7. Complete (success message)
 *
 * Additional for Owner/Chief Supervisor:
 * 8. Review Team Members
 * 9. Verify Organization Certifications
 *
 * Reference: agent-os/specs/2026-01-22-user-onboarding/tasks.md (Task 9.3)
 */
@Component({
  selector: 'app-onboarding-wizard-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, NgxMaskDirective, MaxLengthDirective, UsStateSelectComponent],
  templateUrl: './onboarding-wizard-modal.component.html',
  styleUrls: ['./onboarding-wizard-modal.component.scss']
})
export class OnboardingWizardModalComponent {
  private progressService = inject(MemberOnboardingProgressService);
  private orgContext = inject(OrganizationContextService);
  private organizationService = inject(OrganizationService);
  private certificationService = inject(CertificationService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);
  private authApiService = inject(AuthApiService);

  // Current organization ID (set during load)
  private currentOrgId: string | null = null;

  @ViewChild('modalElement') modalElement?: ElementRef;

  // Output events
  @Output() completed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // State signals
  isOpen = signal(false);
  currentStep = signal(1);
  totalSteps = signal(7); // Default for regular members
  isProcessing = signal(false);
  progress = signal<MemberOnboardingProgress | null>(null);
  safetyAcknowledged = signal(false);

  // File upload states (new uploads)
  part107File = signal<File | null>(null);
  medicalFile = signal<File | null>(null);
  insuranceFile = signal<File | null>(null);
  stateLicenseFile = signal<File | null>(null);

  // Existing document states (already uploaded)
  existingPart107Doc = signal<{ fileName: string; url: string } | null>(null);
  existingMedicalDoc = signal<{ fileName: string; url: string } | null>(null);
  existingInsuranceDoc = signal<{ fileName: string; url: string } | null>(null);
  existingStateLicenseDoc = signal<{ fileName: string; url: string } | null>(null);

  // Form validity signals (needed because FormGroup.valid is not a signal)
  profileFormValid = signal(false);
  part107FormValid = signal(false);
  medicalFormValid = signal(false);
  insuranceFormValid = signal(false);
  stateLicenseFormValid = signal(false);

  // Profile form
  profileForm: FormGroup = this.fb.group({
    phone: ['', [Validators.required]],
    dateOfBirth: [''], // Optional
    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    city: ['', [Validators.required]],
    state: ['', [Validators.required]],
    zipCode: ['', [Validators.required]]
  });

  // Part 107 certification form
  part107Form: FormGroup = this.fb.group({
    certificateNumber: ['', [Validators.required]],
    expirationDate: ['', [Validators.required]]
  });

  // Medical certification form - expiration required when uploading
  medicalForm: FormGroup = this.fb.group({
    certificateNumber: [''],
    expirationDate: ['', [Validators.required]]
  });

  // Insurance certification form - expiration required when uploading
  insuranceForm: FormGroup = this.fb.group({
    policyNumber: [''],
    expirationDate: ['', [Validators.required]]
  });

  // State license certification form
  stateLicenseForm: FormGroup = this.fb.group({
    licenseNumber: ['', [Validators.required]],
    state: ['', [Validators.required]],
    expirationDate: ['', [Validators.required]]
  });

  constructor() {
    // Subscribe to form status changes to update validity signals
    // This is needed because FormGroup.valid is not a signal
    this.profileForm.statusChanges.subscribe(() => {
      this.profileFormValid.set(this.profileForm.valid);
    });
    this.part107Form.statusChanges.subscribe(() => {
      this.part107FormValid.set(this.part107Form.valid);
    });
    this.medicalForm.statusChanges.subscribe(() => {
      this.medicalFormValid.set(this.medicalForm.valid);
    });
    this.insuranceForm.statusChanges.subscribe(() => {
      this.insuranceFormValid.set(this.insuranceForm.valid);
    });
    this.stateLicenseForm.statusChanges.subscribe(() => {
      this.stateLicenseFormValid.set(this.stateLicenseForm.valid);
    });
  }

  // Computed wizard steps
  steps = computed<WizardStep[]>(() => {
    const prog = this.progress();
    if (!prog) {
      return this.getDefaultSteps();
    }

    return [
      {
        stepNumber: 1,
        title: 'Complete Profile',
        subtitle: 'Add your contact information',
        isRequired: true,
        isCompleted: prog.profileCompleted
      },
      {
        stepNumber: 2,
        title: 'Upload Part 107 Certificate',
        subtitle: 'Required by FAA for commercial operations',
        isRequired: true,
        isCompleted: prog.part107Uploaded
      },
      {
        stepNumber: 3,
        title: 'Upload FAA Medical Certificate',
        subtitle: 'Optional - if applicable',
        isRequired: false,
        isCompleted: prog.medicalUploaded
      },
      {
        stepNumber: 4,
        title: 'Upload Insurance Certificate',
        subtitle: 'Optional - if required by organization',
        isRequired: false,
        isCompleted: prog.insuranceUploaded
      },
      {
        stepNumber: 5,
        title: 'Upload State Applicator License',
        subtitle: 'Required for pesticide application',
        isRequired: true,
        isCompleted: prog.stateLicenseUploaded
      },
      {
        stepNumber: 6,
        title: 'Review Safety Guidelines',
        subtitle: 'Important safety information',
        isRequired: true,
        isCompleted: prog.safetyGuidelinesReviewed
      },
      {
        stepNumber: 7,
        title: 'Complete',
        subtitle: 'All set!',
        isRequired: false,
        isCompleted: prog.completedAt !== null
      }
    ];
  });

  // Current step info
  currentStepInfo = computed(() => {
    const allSteps = this.steps();
    const stepNum = this.currentStep();
    return allSteps.find(s => s.stepNumber === stepNum) || allSteps[0];
  });

  // Can proceed to next step
  canProceed = computed(() => {
    const step = this.currentStep();

    // Step-specific validation using validity signals (not form.valid directly)
    switch (step) {
      case 1:
        return this.profileFormValid();
      case 2:
        // Valid if form is valid AND (has new file OR has existing document)
        const hasPart107Doc = this.part107File() !== null || this.existingPart107Doc() !== null;
        return this.part107FormValid() && hasPart107Doc;
      case 3:
        // Medical is optional, but if file selected, expiration is required
        if (this.medicalFile()) {
          return this.medicalFormValid();
        }
        return true; // Can skip if no file selected
      case 4:
        // Insurance is optional, but if file selected, expiration is required
        if (this.insuranceFile()) {
          return this.insuranceFormValid();
        }
        return true; // Can skip if no file selected
      case 5:
        // Valid if form is valid AND (has new file OR has existing document)
        const hasStateLicenseDoc = this.stateLicenseFile() !== null || this.existingStateLicenseDoc() !== null;
        return this.stateLicenseFormValid() && hasStateLicenseDoc;
      case 6:
        return this.safetyAcknowledged();
      default:
        return true;
    }
  });

  /**
   * Open wizard modal, optionally jumping to a specific step.
   * @param startStep Optional step number (1-7) to open directly. If omitted, opens at the first incomplete step.
   */
  async open(startStep?: number): Promise<void> {
    await this.loadProgress();
    await this.loadProfileData();
    await this.loadExistingCertifications();
    this.currentStep.set(startStep ?? this.getFirstIncompleteStep());
    this.isOpen.set(true);
  }

  /**
   * Close wizard modal
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  /**
   * Go to next step
   */
  async nextStep(): Promise<void> {
    const step = this.currentStep();

    // Process current step before advancing - stop if error
    try {
      await this.processStep(step);
    } catch {
      return; // Error already shown via toast in processStep
    }

    // Advance to next step
    if (step < this.totalSteps()) {
      this.currentStep.set(step + 1);
    }

    // Check if completed
    if (step === this.totalSteps()) {
      await this.completeOnboarding();
    }
  }

  /**
   * Go to previous step
   */
  previousStep(): void {
    const step = this.currentStep();
    if (step > 1) {
      this.currentStep.set(step - 1);
    }
  }

  /**
   * Skip current step (only for optional steps)
   */
  skipStep(): void {
    const stepInfo = this.currentStepInfo();
    if (!stepInfo.isRequired) {
      const step = this.currentStep();
      if (step < this.totalSteps()) {
        this.currentStep.set(step + 1);
      }
    }
  }

  /**
   * Handle file selection for Part 107
   */
  onPart107FileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.part107File.set(input.files[0]);
    }
  }

  /**
   * Handle file selection for Medical
   */
  onMedicalFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.medicalFile.set(input.files[0]);
    }
  }

  /**
   * Handle file selection for Insurance
   */
  onInsuranceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.insuranceFile.set(input.files[0]);
    }
  }

  /**
   * Handle file selection for State License
   */
  onStateLicenseFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.stateLicenseFile.set(input.files[0]);
    }
  }

  /**
   * Load onboarding progress
   * Gets organization from context or fetches it via API
   */
  private async loadProgress(): Promise<void> {
    try {
      // Get org ID from context, or fetch from API if not set
      let orgId = this.orgContext.currentOrganizationId();

      if (!orgId) {
        // Try to get the organization from the API
        const org = await this.organizationService.getMyOrganization();
        if (org) {
          orgId = org.id;
          // Context is automatically set by the service
        }
      }

      if (!orgId) {
        throw new Error('No organization found');
      }

      // Store the org ID for later use
      this.currentOrgId = orgId;

      const progress = await this.progressService.getProgress(orgId);
      this.progress.set(progress);
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
      this.toastService.error('Failed to load onboarding progress');
    }
  }

  /**
   * Load existing profile data and pre-populate form
   */
  private async loadProfileData(): Promise<void> {
    try {
      const { profile } = await this.authApiService.getCurrentUser();

      if (profile) {
        // Pre-populate profile form with existing data
        this.profileForm.patchValue({
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
          addressLine1: profile.address || '',
          city: profile.city || '',
          state: profile.state || '',
          zipCode: profile.zipCode || ''
        });

        // Update validity signal after patching form
        this.profileFormValid.set(this.profileForm.valid);
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
      // Don't show error - form will just be empty
    }
  }

  /**
   * Load existing certifications and pre-populate forms
   */
  private async loadExistingCertifications(): Promise<void> {
    try {
      const certifications = await this.certificationService.getProfileCertifications();

      for (const cert of certifications) {
        switch (cert.certificationType) {
          case 'faa-part-107':
            this.part107Form.patchValue({
              certificateNumber: cert.certificateNumber || '',
              expirationDate: cert.expirationDate ? cert.expirationDate.split('T')[0] : ''
            });
            // Track existing document
            if (cert.documentUrl) {
              this.existingPart107Doc.set({
                fileName: cert.documentFileName || 'Uploaded document',
                url: await this.getDownloadUrl(cert.id)
              });
            }
            break;
          case 'medical':
            this.medicalForm.patchValue({
              certificateNumber: cert.certificateNumber || '',
              expirationDate: cert.expirationDate ? cert.expirationDate.split('T')[0] : ''
            });
            // Track existing document
            if (cert.documentUrl) {
              this.existingMedicalDoc.set({
                fileName: cert.documentFileName || 'Uploaded document',
                url: await this.getDownloadUrl(cert.id)
              });
            }
            break;
          case 'insurance':
            this.insuranceForm.patchValue({
              policyNumber: cert.certificateNumber || '',
              expirationDate: cert.expirationDate ? cert.expirationDate.split('T')[0] : ''
            });
            // Track existing document
            if (cert.documentUrl) {
              this.existingInsuranceDoc.set({
                fileName: cert.documentFileName || 'Uploaded document',
                url: await this.getDownloadUrl(cert.id)
              });
            }
            break;
          case 'state-applicator':
            this.stateLicenseForm.patchValue({
              licenseNumber: cert.certificateNumber || '',
              state: cert.state || '',
              expirationDate: cert.expirationDate ? cert.expirationDate.split('T')[0] : ''
            });
            // Track existing document
            if (cert.documentUrl) {
              this.existingStateLicenseDoc.set({
                fileName: cert.documentFileName || 'Uploaded document',
                url: await this.getDownloadUrl(cert.id)
              });
            }
            break;
        }
      }

      // Update validity signals after patching forms
      this.part107FormValid.set(this.part107Form.valid);
      this.medicalFormValid.set(this.medicalForm.valid);
      this.insuranceFormValid.set(this.insuranceForm.valid);
      this.stateLicenseFormValid.set(this.stateLicenseForm.valid);
    } catch (error) {
      console.error('Failed to load certifications:', error);
      // Don't show error - forms will just be empty
    }
  }

  /**
   * Process step before advancing
   */
  private async processStep(stepNumber: number): Promise<void> {
    try {
      this.isProcessing.set(true);

      switch (stepNumber) {
        case 1:
          await this.saveProfile();
          break;
        case 2:
          await this.uploadPart107();
          break;
        case 3:
          if (this.medicalFile()) {
            await this.uploadMedical();
          }
          break;
        case 4:
          if (this.insuranceFile()) {
            await this.uploadInsurance();
          }
          break;
        case 5:
          if (this.stateLicenseFile()) {
            await this.uploadStateLicense();
          }
          break;
        case 6:
          await this.markSafetyReviewed();
          break;
      }

      // Reload progress after processing
      await this.loadProgress();
    } catch (error: any) {
      console.error('Failed to process step:', error);
      this.toastService.error(error?.message || 'Failed to save progress');
      throw error;
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Save profile information
   * Calls PUT /auth/profile and marks profile completed in onboarding progress
   */
  private async saveProfile(): Promise<void> {
    if (!this.profileForm.valid) return;

    const formData = this.profileForm.value;

    // Map form fields to API format
    const profileData = {
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth,
      address: formData.addressLine1 + (formData.addressLine2 ? ', ' + formData.addressLine2 : ''),
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode
    };

    try {
      // Update profile via auth API (sends only the allowlisted fields)
      await this.authApiService.updateProfile(profileData);

      // Mark profile as completed in onboarding progress
      if (this.currentOrgId) {
        await this.progressService.markProfileCompleted(this.currentOrgId);
      }

      this.toastService.success('Profile saved successfully');
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      throw new Error(ApiError.fromHttpError(error).errors[0] ?? 'Failed to save profile');
    }
  }

  /**
   * Upload Part 107 certificate
   */
  private async uploadPart107(): Promise<void> {
    const file = this.part107File();
    if (!file || !this.part107Form.valid) return;

    try {
      // Upload file to S3
      const { url, fileName } = await this.certificationService.uploadCertificationDocument(file);

      // Create certification record
      const createdCert = await this.certificationService.createCertification({
        certificationType: 'faa-part-107',
        ownerType: 'profile',
        certificateNumber: this.part107Form.value.certificateNumber,
        issuedDate: new Date().toISOString().split('T')[0], // Default to today
        expirationDate: this.part107Form.value.expirationDate,
        issuingAuthority: 'FAA',
        documentUrl: url,
        documentFileName: fileName
      });

      this.existingPart107Doc.set({
        fileName: fileName || 'Uploaded document',
        url: await this.getDownloadUrl(createdCert.id)
      });
      this.part107File.set(null)

      this.toastService.success('Part 107 certificate uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload Part 107:', error);
      throw new Error(error.message || 'Failed to upload Part 107 certificate');
    }
  }

  private async getDownloadUrl(id: string): Promise<string> {
    try {
      return await this.certificationService.getDownloadUrl(id)
    } catch {
      console.error('Could not download url')
      return '';
    }
  }

  /**
   * Upload Medical certificate
   */
  private async uploadMedical(): Promise<void> {
    const file = this.medicalFile();
    if (!file) return;

    // Skip if no form data entered
    if (!this.medicalForm.value.certificateNumber && !this.medicalForm.value.expirationDate) {
      return;
    }

    try {
      // Upload file to S3
      const { url, fileName } = await this.certificationService.uploadCertificationDocument(file);

      // Create certification record
      const createdCert = await this.certificationService.createCertification({
        certificationType: 'medical',
        ownerType: 'profile',
        certificateNumber: this.medicalForm.value.certificateNumber || 'N/A',
        issuedDate: new Date().toISOString().split('T')[0],
        expirationDate: this.medicalForm.value.expirationDate || undefined,
        issuingAuthority: 'FAA',
        documentUrl: url,
        documentFileName: fileName
      });

      this.existingMedicalDoc.set({
        fileName: fileName || 'Uploaded document',
        url: await this.getDownloadUrl(createdCert.id)
      });
      this.medicalFile.set(null);

      this.toastService.success('Medical certificate uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload Medical cert:', error);
      throw new Error(error.message || 'Failed to upload Medical certificate');
    }
  }

  /**
   * Upload Insurance certificate
   */
  private async uploadInsurance(): Promise<void> {
    const file = this.insuranceFile();
    if (!file) return;

    // Skip if no form data entered
    if (!this.insuranceForm.value.policyNumber && !this.insuranceForm.value.expirationDate) {
      return;
    }

    try {
      // Upload file to S3
      const { url, fileName } = await this.certificationService.uploadCertificationDocument(file);

      // Create certification record
      const createdCert = await this.certificationService.createCertification({
        certificationType: 'insurance',
        ownerType: 'profile',
        certificateNumber: this.insuranceForm.value.policyNumber || 'N/A',
        issuedDate: new Date().toISOString().split('T')[0],
        expirationDate: this.insuranceForm.value.expirationDate || undefined,
        issuingAuthority: 'Insurance Provider',
        documentUrl: url,
        documentFileName: fileName
      });

      this.existingInsuranceDoc.set({
        fileName: fileName || 'Uploaded document',
        url: await this.getDownloadUrl(createdCert.id)
      });
      this.insuranceFile.set(null);

      this.toastService.success('Insurance certificate uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload Insurance cert:', error);
      throw new Error(error.message || 'Failed to upload Insurance certificate');
    }
  }

  /**
   * Upload State License
   */
  private async uploadStateLicense(): Promise<void> {
    const file = this.stateLicenseFile();
    if (!file || !this.stateLicenseForm.valid) return;

    try {
      // Upload file to S3
      const { url, fileName } = await this.certificationService.uploadCertificationDocument(file);

      // Create certification record
      const createdCert = await this.certificationService.createCertification({
        certificationType: 'state-applicator',
        ownerType: 'profile',
        certificateNumber: this.stateLicenseForm.value.licenseNumber,
        issuedDate: new Date().toISOString().split('T')[0],
        expirationDate: this.stateLicenseForm.value.expirationDate || undefined,
        issuingAuthority: 'State of ' + (this.stateLicenseForm.value.state || 'Unknown'),
        state: this.stateLicenseForm.value.state || undefined,
        documentUrl: url,
        documentFileName: fileName
      });

      this.existingStateLicenseDoc.set({
        fileName: fileName || 'Uploaded document',
        url: await this.getDownloadUrl(createdCert.id)
      });
      this.stateLicenseFile.set(null);

      this.toastService.success('State license uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload State License:', error);
      throw new Error(error.message || 'Failed to upload State license');
    }
  }

  /**
   * Mark safety guidelines as reviewed
   */
  private async markSafetyReviewed(): Promise<void> {
    if (!this.safetyAcknowledged()) return;

    if (!this.currentOrgId) throw new Error('No organization selected');

    await this.progressService.markSafetyReviewed(this.currentOrgId);
    this.toastService.success('Safety guidelines marked as reviewed');
  }

  /**
   * Complete onboarding
   */
  private async completeOnboarding(): Promise<void> {
    this.toastService.success('Congratulations! Your onboarding is complete');
    this.isOpen.set(false);
    this.completed.emit();
  }

  /**
   * Get first incomplete step
   */
  private getFirstIncompleteStep(): number {
    const allSteps = this.steps();
    const incomplete = allSteps.find(s => !s.isCompleted);
    return incomplete?.stepNumber || 1;
  }

  /**
   * Get default steps before progress loaded
   */
  private getDefaultSteps(): WizardStep[] {
    return [
      { stepNumber: 1, title: 'Complete Profile', subtitle: '', isRequired: true, isCompleted: false },
      { stepNumber: 2, title: 'Upload Part 107 Certificate', subtitle: '', isRequired: true, isCompleted: false },
      { stepNumber: 3, title: 'Upload FAA Medical Certificate', subtitle: '', isRequired: false, isCompleted: false },
      { stepNumber: 4, title: 'Upload Insurance Certificate', subtitle: '', isRequired: false, isCompleted: false },
      { stepNumber: 5, title: 'Upload State Applicator License', subtitle: '', isRequired: true, isCompleted: false },
      { stepNumber: 6, title: 'Review Safety Guidelines', subtitle: '', isRequired: true, isCompleted: false },
      { stepNumber: 7, title: 'Complete', subtitle: '', isRequired: false, isCompleted: false }
    ];
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }
}
